// Component to display tool call status messages
import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ImageWithPlaceholder from './ImageWithPlaceholder';
import '../styles/ImagePlaceholder.css';

interface ToolCallMessageProps {
  content: string;
}

const ToolCallMessage: React.FC<ToolCallMessageProps> = ({ content }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Memoize markdown components to prevent re-creation on every render
  const markdownComponents = useMemo(() => ({
    // Custom renderers for LEGO-specific content
    img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) => (
      <ImageWithPlaceholder src={src} alt={alt} className="message-image" />
    ),
    table: ({ children, ...props }: React.TableHTMLAttributes<HTMLTableElement>) => (
      <div className="message-table-wrapper">
        <table {...props}>{children}</table>
      </div>
    ),
    a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
      <a
        href={href}
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        className="message-link"
      >
        {children}
      </a>
    )
  }), []);


  // Check if it's a tool call start message
  const isToolStart = content.includes('🔧 Calling tool:') || content.includes('🔧 Call to tool:');
  const isToolEnd = content.includes('finished with result:');
  const isToolError = content.includes('❌ Tool call failed:');


  // For tool end messages with results, extract and format
  if (isToolEnd) {
    const parts = content.split('finished with result:');
    const header = parts[0];
    const result = parts[1]?.trim();

    return (
      <div className="tool-call-message">
        <div className="tool-call-header">
          <button
            className="tool-call-toggle"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
          >
            <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
            <span>{header}finished</span>
          </button>
        </div>
        {isExpanded && result && (
          <div className="tool-call-result">
            {renderResult(result, markdownComponents)}
          </div>
        )}
      </div>
    );
  }

  // For tool start messages with parameters
  if (isToolStart && content.includes('with parameters:')) {
    const parts = content.split('with parameters:');
    const header = parts[0];
    const params = parts[1]?.trim();

    return (
      <div className="tool-call-message">
        <div className="tool-call-header">
          <button
            className="tool-call-toggle"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
          >
            <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
            <span>{header}</span>
          </button>
        </div>
        {isExpanded && params && (
          <div className="tool-call-params">
            <h5>Parameters:</h5>
            <pre className="tool-json">
              {formatJsonString(params)}
            </pre>
          </div>
        )}
      </div>
    );
  }

  // For simple status messages
  return (
    <div className="tool-call-message tool-call-status">
      <span className={isToolError ? 'tool-error' : 'tool-info'}>
        {content}
      </span>
    </div>
  );
};

// Helper function to render tool call results
function renderResult(result: string, markdownComponents: Record<string, React.ComponentType<Record<string, unknown>>>) {
  try {
    // Try to parse as JSON
    const parsed = JSON.parse(result);

    // If it's a JSON string (starts and ends with quotes), it might be markdown
    if (typeof parsed === 'string') {
      // It's a JSON string, render as markdown
      return (
        <div className="tool-markdown">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {parsed}
          </ReactMarkdown>
        </div>
      );
    } else {
      // It's a JSON object, format as JSON
      return (
        <pre className="tool-json">
          {JSON.stringify(parsed, null, 2)}
        </pre>
      );
    }
  } catch {
    // If not valid JSON, render as plain text
    return (
      <pre className="tool-json">
        {result}
      </pre>
    );
  }
}

// Helper function to format JSON strings for display
function formatJsonString(jsonString: string) {
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return jsonString;
  }
}

export default ToolCallMessage;